/* eslint-disable jsx-a11y/label-has-associated-control */
import transparentize from 'polished/lib/color/transparentize';
import React, { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { UseAutoComplete } from '../../hooks/useAutoComplete';
import { useCurrencies } from '../../hooks/useCurrencies';
import { UseSearchQueryVariables } from '../../hooks/useSearch';
import { SearchCategory, TypeOfSearch } from '../../types/enums';
import { Kfh_PropertySearchMetaType, Kfh_SearchFacetType } from '../../types/graphqlTypes';
import { CurrencyCodeType, getCurrencyName } from '../../utils/getCurrencyName';
import { getOptions } from '../../utils/getOptions';
import { Divider } from '../Divider';
import { ExpanderPanel } from '../ExpanderPanel';
import { ExpanderPanelGroup } from '../ExpanderPanelGroup';
import { AutoComplete } from '../forms/Autocomplete';
import { Checkbox } from '../forms/Checkbox';
import { Select } from '../forms/Select';
import { Loader } from '../Loader';
import { Panel } from '../Panel';
import { Spacer } from '../Spacer';

interface SearchFacetsProps {
  facets: Kfh_SearchFacetType;
  category: SearchCategory;
  parameters: Kfh_PropertySearchMetaType;
  loading: boolean;
  multiSearch?: { multiSearch: string; name: string };
  searchType: TypeOfSearch;
  activeFilters: Partial<UseSearchQueryVariables>;
}

const StyledLoaderWrap = styled.div`
  ${({ theme: { space, colors, layers } }) => css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${space.lg};
    background-color: ${transparentize(0.2, colors.white.default)};
    z-index: ${layers.bladeOverlay};
  `};
`;

const StyledPanel = styled(Panel)`
  border-top: 0;
`;

const StyledSpacer = styled(Spacer)`
  position: relative;
`;
const facetMap = {
  bathrooms: 'Bathrooms',
  bedrooms: 'Bedrooms',
  propertyType: 'Property Type',
  propertyStyle: 'Property Style',
  receptionRooms: 'Reception Rooms',
  receptions: 'Receptions',
  riverside: 'Riverside',
  tenure: 'Tenure',
  furnishState: 'Furnishings',
  areas: 'Locations',
};
export const SearchFacets = ({
  facets,
  category,
  parameters,
  loading,
  multiSearch,
  searchType = TypeOfSearch.Residential,
  activeFilters,
}: SearchFacetsProps) => {
  const {
    register,
    watch,
    control,
    formState: { isSubmitting },
  } = useFormContext();
  const { handleSetCurrency, current } = useCurrencies();

  const {
    autoCompleteSuggestions,
    handleAutoComplete,
    loading: autoCompleteLoading,
  } = UseAutoComplete({ category, type: 'BASIC' });

  const formData = watch();

  const currencyName = getCurrencyName(current?.code as CurrencyCodeType);
  const currencySaleType = category.toLowerCase();

  const currentPriceLowest = watch('priceLowest');
  const currentPriceHighest = watch('priceHighest');

  const priceLowest = useMemo(
    () =>
      parameters?.[`${currencySaleType}LowestPrice${currencyName}`].filter(({ value }) => value <= currentPriceHighest),
    [currencyName, currencySaleType, currentPriceLowest, parameters]
  );

  const priceHighest = useMemo(
    () =>
      parameters?.[`${currencySaleType}HighestPrice${currencyName}`].filter(({ value }) => value >= currentPriceLowest),
    [currencyName, currencySaleType, currentPriceLowest, parameters]
  );

  return (
    <StyledSpacer>
      {loading && (
        <StyledLoaderWrap>
          <Loader />
          loading
        </StyledLoaderWrap>
      )}
      {searchType === TypeOfSearch.Residential && (
        <Controller
          name="multiSearch"
          control={control}
          render={({ field: { onChange } }) => (
            <AutoComplete
              defaultValue={multiSearch?.name}
              suggestions={autoCompleteSuggestions}
              handleAutoComplete={handleAutoComplete}
              handleSelectValue={(newValue) => onChange(newValue)}
              loading={autoCompleteLoading}
              placeholder="Location"
            />
          )}
        />
      )}
      <div>
        <ExpanderPanelGroup as="ul">
          {searchType === TypeOfSearch.Residential && (
            <ExpanderPanel as="li" title="Price">
              <Spacer spacing="half">
                <Select
                  id="priceLowest"
                  label="Min Price"
                  disabled={isSubmitting}
                  noPlaceholder
                  {...register('priceLowest', {
                    valueAsNumber: true,
                  })}
                  options={getOptions(priceLowest)}
                />
                <Select
                  id="priceHighest"
                  label="Max Price"
                  disabled={isSubmitting}
                  noPlaceholder
                  {...register('priceHighest', {
                    valueAsNumber: true,
                  })}
                  options={getOptions(priceHighest)}
                />
              </Spacer>
            </ExpanderPanel>
          )}
          {facets &&
            Object.entries(facets)
              .filter(([key]) => key !== '__typename')
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              .filter(([key, items]) => key.toLowerCase() !== 'riverside' && Array.isArray(items) && items?.length > 0)
              .map(([key, items]) => {
                // is filtered by user
                const isFiltered = Object.prototype.hasOwnProperty.call(activeFilters, key);

                let countChecked = 0;

                if (isFiltered && formData[key] && Array.isArray(formData[key]) && formData[key]?.length >= 1) {
                  countChecked = formData[key]?.length;
                }
                // console.log(key, facetMap[key], countChecked, formData[key], items);

                return (
                  <ExpanderPanel
                    title={`${facetMap[key] ?? ''}${countChecked >= 1 ? ` (${countChecked})` : ''}`}
                    as="li"
                    key={key}
                  >
                    <Spacer spacing="quarter">
                      {Array.isArray(items) &&
                        items?.map(({ facet, value, count }) => (
                          <Controller
                            key={`${key}-${value ?? facet}`}
                            name={key}
                            control={control}
                            render={({ field }) => {
                              let label = `${value ?? facet}`;
                              if (key === 'bedrooms' && label === '0') {
                                label = 'Studio';
                              }
                              return (
                                <Checkbox
                                  label={`${label} (${count})`}
                                  id={`${key}-${facet}`}
                                  value={facet}
                                  disabled={isSubmitting}
                                  checked={
                                    field?.value && Array.isArray(field.value) && field?.value?.length >= 1
                                      ? field.value.some((existingValue) => existingValue === facet)
                                      : field?.value === facet
                                  }
                                  onChange={(event) => {
                                    if (event.target.checked) {
                                      if (Array.isArray(field.value)) {
                                        field.onChange([...field.value, event.target.value]);
                                      } else {
                                        field.onChange([event.target.value]);
                                      }
                                    } else {
                                      field.onChange(field?.value?.filter((item) => item !== event.target.value));
                                    }
                                  }}
                                />
                              );
                            }}
                          />
                        ))}
                    </Spacer>
                  </ExpanderPanel>
                );
              })}
        </ExpanderPanelGroup>
        {searchType === TypeOfSearch.Residential && (
          <StyledPanel border>
            <Spacer spacing="half">
              <Checkbox label="Include new homes" id="newhomes" {...register('newhomes')} disabled={isSubmitting} />
              {category !== SearchCategory.Rental && (
                <>
                  <Checkbox
                    label="Include under offer and contracts exchanged"
                    id="underoffer"
                    {...register('underoffer')}
                    disabled={isSubmitting}
                  />
                  <Divider />
                </>
              )}
              {facets?.riverside && facets.riverside?.length >= 1 && (
                <Checkbox
                  label="Show only Riverside properties"
                  id="riverside"
                  {...register('riverside')}
                  disabled={isSubmitting}
                />
              )}
              {category !== SearchCategory.Rental && (
                <Checkbox
                  label="Show only sold properties"
                  id="unavailable"
                  {...register('unavailable')}
                  disabled={isSubmitting}
                />
              )}

              {category === SearchCategory.Rental && (
                <>
                  <Checkbox
                    label="Show only let properties"
                    id="unavailable"
                    {...register('unavailable')}
                    disabled={isSubmitting}
                  />
                  <Divider />
                  <Checkbox label="Managed by KFH" id="kfh" {...register('kfh')} disabled={isSubmitting} />
                  <Checkbox label="Long let" id="longlet" {...register('longlet')} disabled={isSubmitting} />
                  <Checkbox
                    label="Short let (up to 6 months)"
                    id="shortlet"
                    {...register('shortlet')}
                    disabled={isSubmitting}
                  />
                </>
              )}
            </Spacer>
          </StyledPanel>
        )}
      </div>
    </StyledSpacer>
  );
};
